// Pure, exhaustively-unit-testable sanitizers for the LogRocket session-replay
// integration wired up in `index.tsx`. Kept free of any LogRocket import so the
// redaction logic can be tested without booting the recorder.
//
// SECURITY: LogRocket is third-party session replay. Anything these functions
// let through is uploaded off-box in cleartext, so they are the last line of
// defense for two leak classes:
//   1. the one-time share-link token in URLs (`/s/<token>`, `/share/<token>`)
//   2. plaintext credentials POSTed to the access broker (`/broker`)

// The share-link token is the lookup capability; the page path `/s/<token>` and
// the request path `/share/<token>` are the only two shapes that carry it.
// Replace the trailing token segment with [REDACTED]; leave everything else
// untouched.
export function redactShareToken(url: string): string {
  return url.replace(/(\/(?:s|share)\/)[^/?#]+/g, '$1[REDACTED]');
}

// Exact key-name allowlist used for EVERY non-broker request body. This is the
// legacy behavior: it only redacts values whose JSON key matches one of these
// well-known secret names.
// The value pattern `"(?:[^"\\]|\\.)*"` matches a full JSON string INCLUDING
// escaped quotes (`\"`) and escaped backslashes, so a secret value containing an
// escaped quote no longer truncates the match and leaks its tail. A naive
// `"[^"]*"` would stop at the first `\"` and let the remainder through.
const SECRET_KEY_RE =
  /"(password|secret|token|api_key|apikey|access_token|refresh_token|client_secret|private_key)"\s*:\s*"(?:[^"\\]|\\.)*"/gi;

// True when a request targets the credential broker. The broker request body
// carries raw operator-typed credential KVs under `parameters`, whose keys are
// arbitrary (e.g. `totp_secret`, `bearer`, `api_token`, or anything the operator
// types) and therefore CANNOT be covered by the key-name allowlist above. Match
// the `/broker` path segment on any body-bearing method — the broker body must
// never legitimately appear in replay, so we err on the side of redaction.
function isBrokerRequest(url: string, method: string | undefined): boolean {
  const m = (method ?? '').toUpperCase();
  const bodyBearing =
    m === '' || m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
  // `(?:[/?#]|$)` guards against matching `/brokerage` and friends.
  return bodyBearing && /\/broker(?:[/?#]|$)/.test(url);
}

// Redact the ENTIRE `parameters` object value from a broker body — shape-based,
// not key-name-based, so no operator-typed credential key can slip through. If
// the body cannot be parsed as the expected JSON object, redact the whole body
// (conservative: never let an unrecognized broker body reach replay).
function redactBrokerBody(body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed)
    ) {
      const obj = parsed as Record<string, unknown>;
      // Match the parameters key CASE-INSENSITIVELY. The broker body is built
      // with a CAPITAL `Parameters` at nearly every production call site
      // (complex-login, add-seed, credential modals, AD credentials); only the
      // HITL interaction path uses lowercase `parameters`. A lowercase-only
      // check would let the capital-`Parameters` plaintext through to replay.
      for (const key of Object.keys(obj)) {
        if (key.toLowerCase() === 'parameters') {
          obj[key] = '[REDACTED]';
        }
      }
      // Belt-and-suspenders: also apply the allowlist to any top-level secret
      // keys that live outside `parameters`.
      return JSON.stringify(obj).replace(SECRET_KEY_RE, '"$1":"[REDACTED]"');
    }
  } catch {
    // fall through to total redaction
  }
  return '[REDACTED]';
}

/**
 * Sanitizes a request body string before it is uploaded to LogRocket.
 *
 * - For any `/broker` request, the whole `parameters` object is redacted
 *   (shape-based) so arbitrary operator-typed credential keys cannot leak.
 * - For every other request, the legacy key-name allowlist is applied so
 *   well-known secret fields are redacted and nothing else regresses.
 */
export function sanitizeRequestBody(
  url: string,
  method: string | undefined,
  body: string
): string {
  if (isBrokerRequest(url, method)) {
    return redactBrokerBody(body);
  }
  return body.replace(SECRET_KEY_RE, '"$1":"[REDACTED]"');
}
